import { useState, useEffect, useRef, useCallback } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { MapPinIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface LocationValue {
  location?: string;
  latitude?: number;
  longitude?: number;
}

interface LocationPickerProps {
  apiKey: string;
  value: LocationValue;
  onChange: (value: { location: string; latitude: number; longitude: number }) => void;
  placeholder?: string;
  mapHeight?: string;
  className?: string;
  label?: string;
  error?: string;
}

// The location inputs are rendered inside parent <form> elements (e.g., the
// activity create/edit form). Pressing Enter in a text input triggers the
// browser's implicit form submission, which prematurely submits the form and
// kicks the user out of it (VIS-147). Suppress Enter's default behavior so it
// only confirms an autocomplete suggestion (Google's place_changed still fires).
const preventEnterSubmit = (event: React.KeyboardEvent<HTMLInputElement>) => {
  if (event.key === 'Enter') {
    event.preventDefault();
  }
};

// Default center (US center) as fallback
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 4;
const SELECTED_ZOOM = 15;

interface MapWithSearchProps {
  value: LocationValue;
  onChange: (value: { location: string; latitude: number; longitude: number }) => void;
  placeholder?: string;
  mapHeight?: string;
}

const MapWithSearch: React.FC<MapWithSearchProps> = ({
  value,
  onChange,
  placeholder = 'Search for a location...',
  mapHeight = '300px',
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mapInstanceRef = useRef<google.maps.Map>();
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement>();
  const autocompleteRef = useRef<google.maps.places.Autocomplete>();
  const geocoderRef = useRef<google.maps.Geocoder>();

  const [searchValue, setSearchValue] = useState(value.location ?? '');
  const [isLoading, setIsLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>(
    value.latitude && value.longitude
      ? { lat: value.latitude, lng: value.longitude }
      : DEFAULT_CENTER
  );
  const [mapZoom, setMapZoom] = useState(
    value.latitude && value.longitude ? SELECTED_ZOOM : DEFAULT_ZOOM
  );

  // Reverse geocode a position to get an address
  const reverseGeocode = useCallback(async (position: google.maps.LatLngLiteral): Promise<string> => {
    geocoderRef.current ??= new google.maps.Geocoder();

    try {
      const response = await geocoderRef.current.geocode({ location: position });
      if (response.results?.[0]) {
        return response.results[0].formatted_address;
      }
    } catch (error) {
      console.error('Geocoding failed:', error);
    }
    return `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`;
  }, []);

  // Update marker position
  const updateMarker = useCallback(async (position: google.maps.LatLngLiteral, getAddress = true) => {
    if (markerRef.current) {
      markerRef.current.position = position;
    }

    if (mapInstanceRef.current) {
      mapInstanceRef.current.panTo(position);
      mapInstanceRef.current.setZoom(SELECTED_ZOOM);
    }

    if (getAddress) {
      setIsLoading(true);
      const address = await reverseGeocode(position);
      setSearchValue(address);
      setIsLoading(false);

      onChange({
        location: address,
        latitude: position.lat,
        longitude: position.lng,
      });
    }
  }, [onChange, reverseGeocode]);

  // Initialize map and marker
  useEffect(() => {
    const initMap = async () => {
      if (!mapRef.current || mapInstanceRef.current) {
        return;
      }

      // Create map
      mapInstanceRef.current = new google.maps.Map(mapRef.current, {
        center: mapCenter,
        zoom: mapZoom,
        mapId: 'LOCATION_PICKER_MAP',
        mapTypeId: 'roadmap',
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
          position: google.maps.ControlPosition.TOP_RIGHT,
        },
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
      });

      // Create marker
      const { AdvancedMarkerElement, PinElement } = (await google.maps.importLibrary(
        'marker'
      )) as google.maps.MarkerLibrary;

      const pinElement = new PinElement({
        background: '#EF4444',
        borderColor: '#FFFFFF',
        glyphColor: '#FFFFFF',
        scale: 1.2,
      });

      const marker = new AdvancedMarkerElement({
        map: value.latitude && value.longitude ? mapInstanceRef.current : undefined,
        position: value.latitude && value.longitude
          ? { lat: value.latitude, lng: value.longitude }
          : mapCenter,
        title: 'Selected Location',
        content: pinElement.element,
        gmpDraggable: true,
      });
      markerRef.current = marker;

      // Handle marker drag
      marker.addListener('dragend', async (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
          const position = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
          };
          await updateMarker(position);
        }
      });

      // Handle map click
      mapInstanceRef.current.addListener('click', async (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
          const position = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
          };

          // Show marker if not visible
          if (markerRef.current && !markerRef.current.map && mapInstanceRef.current) {
            markerRef.current.map = mapInstanceRef.current;
          }

          await updateMarker(position);
        }
      });

      // Initialize geocoder
      geocoderRef.current = new google.maps.Geocoder();

      // Initial centering when no stored coordinates exist
      const hasCoords = Boolean(value.latitude && value.longitude);
      const hasLocationText = Boolean(value.location?.trim());

      if (!hasCoords && hasLocationText) {
        // Existing record with an address but no stored coordinates: center the
        // map on the saved address, not on the browser's current position.
        // Display-only — committing coordinates stays a user action (drag/click).
        try {
          const response = await geocoderRef.current.geocode({ address: value.location as string });
          const loc = response.results?.[0]?.geometry?.location;
          if (loc && mapInstanceRef.current) {
            const center = { lat: loc.lat(), lng: loc.lng() };
            setMapCenter(center);
            setMapZoom(12);
            mapInstanceRef.current.setCenter(center);
            mapInstanceRef.current.setZoom(12);
            if (markerRef.current) {
              markerRef.current.position = center;
              markerRef.current.map = mapInstanceRef.current;
            }
          }
        } catch {
          // Geocoding failed (e.g. free-form text); keep default center
        }
      } else if (!hasCoords && navigator.geolocation) {
        // Blank location: fall back to the user's current position
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setMapCenter(userLocation);
            setMapZoom(12);
            if (mapInstanceRef.current) {
              mapInstanceRef.current.setCenter(userLocation);
              mapInstanceRef.current.setZoom(12);
            }
          },
          () => {
            // Geolocation failed, use default
          }
        );
      }
    };

    void initMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Initialize Places Autocomplete
  useEffect(() => {
    const initAutocomplete = async () => {
      if (!searchInputRef.current || autocompleteRef.current) {
        return;
      }

      await google.maps.importLibrary('places');

      autocompleteRef.current = new google.maps.places.Autocomplete(searchInputRef.current, {
        types: ['geocode', 'establishment'],
        fields: ['formatted_address', 'geometry', 'name'],
      });

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current?.getPlace();

        if (place?.geometry?.location) {
          const position = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          };
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          const locationName = place.formatted_address || place.name || '';

          setSearchValue(locationName);

          // Show marker if not visible
          if (markerRef.current) {
            if (!markerRef.current.map && mapInstanceRef.current) {
              markerRef.current.map = mapInstanceRef.current;
            }
            markerRef.current.position = position;
          }

          if (mapInstanceRef.current) {
            mapInstanceRef.current.panTo(position);
            mapInstanceRef.current.setZoom(SELECTED_ZOOM);
          }

          onChange({
            location: locationName,
            latitude: position.lat,
            longitude: position.lng,
          });
        }
      });
    };

    void initAutocomplete();
  }, [onChange]);

  // Update search value when external value changes
  useEffect(() => {
    if (value.location && value.location !== searchValue) {
      setSearchValue(value.location);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally comparing against searchValue without triggering on its change
  }, [value.location]);

  // Update map center and zoom when external coordinates change
  useEffect(() => {
    if (value.latitude && value.longitude && mapInstanceRef.current && markerRef.current) {
      const position = { lat: value.latitude, lng: value.longitude };

      // Update map center and zoom
      mapInstanceRef.current.setCenter(position);
      mapInstanceRef.current.setZoom(SELECTED_ZOOM);

      // Update marker position and ensure it's visible
      markerRef.current.position = position;
      markerRef.current.map ??= mapInstanceRef.current;
    }
  }, [value.latitude, value.longitude]);

  return (
    <div className="space-y-3">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-600 border-t-transparent" />
          ) : (
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          )}
        </div>
        <input
          ref={searchInputRef}
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={preventEnterSubmit}
          placeholder={placeholder}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
        />
      </div>

      {/* Map Container */}
      <div
        ref={mapRef}
        style={{ height: mapHeight }}
        className="w-full rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700"
      />

      {/* Instructions */}
      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
        <MapPinIcon className="h-3 w-3" />
        Search for a location, click on the map, or drag the marker to set the location
      </p>
    </div>
  );
};

const LocationPicker: React.FC<LocationPickerProps> = ({
  apiKey,
  value,
  onChange,
  placeholder,
  mapHeight,
  className,
  label,
  error,
}) => {
  const render = useCallback((status: Status) => {
    switch (status) {
      case Status.LOADING:
        return (
          <div className="space-y-3">
            <div className="w-full h-10 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
            <div
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              style={{ height: mapHeight || '300px' }}
              className="w-full bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse flex items-center justify-center"
            >
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2" />
                <p className="text-sm text-gray-600 dark:text-gray-400">Loading map...</p>
              </div>
            </div>
          </div>
        );
      case Status.FAILURE:
        return (
          <div className="space-y-3">
            <input
              type="text"
              value={value.location ?? ''}
              onChange={(e) => onChange({
                location: e.target.value,
                latitude: value.latitude ?? 0,
                longitude: value.longitude ?? 0
              })}
              onKeyDown={preventEnterSubmit}
              placeholder="Enter location (map unavailable)"
              className="w-full px-4 py-2 border border-red-300 dark:border-red-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
            />
            <div
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              style={{ height: mapHeight || '300px' }}
              className="w-full bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center justify-center"
            >
              <div className="text-center">
                <p className="text-red-600 dark:text-red-400 font-medium">Failed to load map</p>
                <p className="text-sm text-red-500 dark:text-red-300">
                  Please check your internet connection and API key
                </p>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <MapWithSearch
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            mapHeight={mapHeight}
          />
        );
    }
  }, [value, onChange, placeholder, mapHeight]);

  // Check if API key is missing
  if (!apiKey || apiKey === 'demo-key') {
    return (
      <div className={className}>
        {label && (
          <label className="block text-sm font-medium mb-2">
            <MapPinIcon className="w-4 h-4 inline mr-1" />
            {label}
          </label>
        )}
        <input
          type="text"
          value={value.location ?? ''}
          onChange={(e) => onChange({
            location: e.target.value,
            latitude: 0,
            longitude: 0
          })}
          onKeyDown={preventEnterSubmit}
          placeholder="Enter location (map not configured)"
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
        />
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
          Map is not available. Configure VITE_GOOGLE_MAPS_API_KEY to enable location picker.
        </p>
        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
      </div>
    );
  }

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium mb-2">
          <MapPinIcon className="w-4 h-4 inline mr-1" />
          {label}
        </label>
      )}
      <Wrapper
        apiKey={apiKey}
        libraries={['geometry', 'drawing', 'places', 'marker']}
        render={render}
      />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};

export default LocationPicker;
